import { z } from 'zod';
import { uuidV7Schema } from '../uuid.js';
import { appointmentReasonSchema } from './appointment.js';
import { appointmentDurationSchema } from './appointment-duration.js';
/**
 * Le rendez-vous **posé depuis la tablette**, au porte-à-porte d'un contrôle systématique (#268) ou
 * d'une SYAM (#695).
 *
 * ⚠️ **Une écriture directe qui part hors réseau**, et c'est ce qui la sépare du report de
 * rendez-vous de D4 (gate en ligne) : l'agent fixe le créneau avec la personne devant lui, souvent
 * là où le signal manque, et le rendez-vous passe par l'outbox comme un avis de passage.
 *
 * ⚠️ **L'`id` vient de la tablette** (uuid v7, M7), contrairement à `scheduleAppointmentSchema` :
 * c'est lui qui rend le rejeu d'un envoi interrompu inoffensif. Le serveur ne laisse pas ce
 * choix ouvrir l'écriture sur un rendez-vous existant : un `id` déjà en base rend la ligne en place
 * sans rien écrire, et seulement sur la même demande.
 */
/**
 * Les seuls types de contrôle que **le bureau planifie** : la vente et l'enquête. Tous les autres
 * s'affectent à une équipe, et c'est l'agent qui prend le rendez-vous sur place.
 *
 * Décision de GHP du 04/11/2026 (BACK-OFFICE-13) : « seul enquête et vente on garde à planifier »,
 * « ou secteur ». La liste s'est inversée : #695 (29/10) n'y avait ajouté que la SYAM au
 * systématique, et 142 « Avant travaux » affectés sans RDV n'arrivaient sur aucune tablette.
 *
 * ⚠️ **La seule liste qui fasse foi** : la tournée, le rail « À planifier » et ses compteurs la
 * relisent, l'écran n'en tient pas de seconde.
 *
 * ⚠️ `purpose` est du texte libre (M10) : un type absent ou inconnu se fait sur place, il reste
 * donc visible sur la tablette (M21).
 */
export const PLANNED_AT_OFFICE = ['sale', 'investigation'];
/**
 * L'agent prend-il le rendez-vous sur place ? Oui, sauf pour une vente ou une enquête **hors
 * secteur** : une demande dont le lieu appartient à un secteur (`Site.lot_id`, « Secteur » à
 * l'écran) se fait sur place quel que soit son type.
 */
export function isPlannedOnSite(purpose, inSector) {
    return inSector || !PLANNED_AT_OFFICE.includes(purpose ?? '');
}
/** Un champ facultatif de la personne : laissé blanc, il vaut « non communiqué » (`null`). */
const optionalLabelSchema = z
    .string()
    .trim()
    .max(200)
    .nullable()
    .transform((text) => (text === '' ? null : text));
/**
 * Ce que la tablette dépose : le créneau, et la personne avec qui il a été pris.
 *
 * ⚠️ **Nom et téléphone facultatifs, pas d'e-mail** (réunion GHP du 22/09/2026 : « il ne faut pas
 * que ça bloque »). Ils vont au contact du rendez-vous (`Inquiry.appointment_contact_id`, M15).
 *
 * Même exigence de format que `scheduleAppointmentSchema` avant la coercition : une chaîne ISO
 * **avec** fuseau, sans quoi l'heure de la confirmation serait indécidable (M5).
 */
export const recordFieldAppointmentSchema = z.strictObject({
    id: uuidV7Schema,
    inquiryId: uuidV7Schema,
    scheduledAt: z.iso.datetime({ offset: true }).pipe(z.coerce.date()),
    contactName: optionalLabelSchema,
    contactPhone: optionalLabelSchema,
    /**
     * Le créneau que la tablette **voyait en place** quand l'agent a décidé — `null` pour une première
     * pose. Le serveur ne remplace que ce créneau-là : si le bureau en a posé ou déplacé un depuis, la
     * pose est refusée (`409`) plutôt que d'annuler en silence ce que l'usager a reçu par écrit.
     * `nullish` : une entrée mise en file avant ce champ est une première pose.
     */
    replacing: z.iso
        .datetime({ offset: true })
        .pipe(z.coerce.date())
        .nullish()
        .transform((at) => at ?? null),
    /**
     * Le motif d'un **déplacement** (M24) — absent d'un premier créneau. `nullish` : une tablette
     * d'avant ce champ, ou un envoi encore en file, n'en porte pas.
     */
    reason: appointmentReasonSchema.nullish().transform((reason) => reason ?? null),
    /**
     * La durée que l'agent a réglée en déplaçant le rendez-vous (#578, frame 2.1b). Absente — une
     * première pose, ou une tablette d'avant ce champ —, le rendez-vous garde celle qu'il remplace, ou
     * prend celle de son type de contrôle, comme au bureau (`scheduleAppointmentSchema`).
     */
    durationMinutes: appointmentDurationSchema.optional(),
    /** Le contexte libre que l'agent ajoute (frames 2.1 et 2.4), facultatif. */
    comment: z
        .string()
        .trim()
        .max(1000)
        .nullish()
        .transform((text) => (text == null || text === '' ? null : text)),
});
//# sourceMappingURL=field-appointment.js.map